import { ReactNode, ReactElement, useState, useEffect, useCallback } from "react"
import { useStore, Layout, ReferenceArrayField, SingleFieldList, Notification, useAuthState } from "react-admin"
import { useTranslate, useResourceContext, useGetRecordId, useRecordContext, useLocale } from "ra-core"
import { useNavigate, useLocation, Link } from "react-router-dom"
import { AppBar, Tabs, Tab, TabProps, Chip, Menu, MenuItem } from "@mui/material"
import ExpandMore from "@mui/icons-material/ExpandMore"
import ExpandLess from '@mui/icons-material/ExpandLess'
import { styled } from "@mui/material/styles"
import MainMenu from "components/ui/menu"
import List from "@mui/icons-material/List"
import Settings from "@mui/icons-material/Settings"
import Create from "@mui/icons-material/Create"
import { store } from "store"
import { AxiosRequestConfig } from "axios"
import { fetchWithToken } from "api/data"
import { default as routes } from "api/routes"
import { UserProps, UserProfileTypeEntity, UserEntity } from "store.context"
import { UpdateProfileRequest } from "api/user/user.pb"

//import ArrowDropDown from "@mui/icons-material/ArrowDropDown"
//import ArrowDropUp from "@mui/icons-material/ArrowDropUp"

// Константа для ресурсов, которые поддерживают аудит
const AUDIT_ENABLED_RESOURCES = [
    'users',
    'companies', 
    'objects',
    'devices',
    'equipments',
]

interface McsLayoutProps {
    children: ReactNode
}

export interface SubTabProps {
    label: string
    name: string
    resource: string
    id?: string
    icon?: ReactElement
}

interface RecordActionsSubTabsProps {
    resourceParent?: string
    resourceParentId?: string
    subTabs?: SubTabProps[]
    onCreateClick?: () => void // Кастомный обработчик для кнопки создания
}
const MainContainer = styled('div')({
    flex: 1,
    overflow: 'auto',
    // Можно задать фиксированную высоту или оставить flex-рост, если Layout уже настроен как flex-контейнер
    // height: 'calc(100vh - 64px)', // если высота AppBar равна 64px
})

export const McsLayout: React.FC<McsLayoutProps> = ({ children }) => {
    const { authenticated } = useAuthState()
    const locale = useLocale()
    const [ user, setUser ] = useStore<UserProps>('currentUser', undefined)
    const [ , setProfile ] = useStore<UserProfileTypeEntity>('currentProfile', undefined)
    const [ lastLocale, setLastLocale ] = useState<string | null>(null)
    
    const getConfigProfile = (): AxiosRequestConfig => ({
        url: routes.userProfileTypes(),
        method: 'get',
    })

    const handleProfileChange = useCallback((profile: UserProfileTypeEntity) => {
        const configUpdateUserProfile: AxiosRequestConfig = {
            url: routes.setDefaultProfile(),
            method: 'post',
            data: { default_profile: profile.id } as UpdateProfileRequest,
        }
        fetchWithToken(configUpdateUserProfile)
            .then(() => console.debug("default profile saved"))
            .catch((error) => console.error(error))
        setProfile(profile)
    }, [setProfile])

    useEffect(() => {
        if (authenticated && !user) {
            store.subscribe('currentUser', (userStore)=> {
                if (userStore) {
                    const parsedUser = JSON.parse(userStore) as UserEntity
                    
                    fetchWithToken(getConfigProfile())
                        .then((response) => {
                            const profiles = (response.data as UserProfileTypeEntity[]).filter((profile) => parsedUser.profiles.includes(profile.id))
                            const userData = {
                                user: parsedUser,
                                profiles: profiles
                            } as UserProps
                            setUser(userData)
                            setLastLocale(locale) // Устанавливаем текущую локаль при первой загрузке
                            handleProfileChange(profiles.find((prof) => prof.id === parsedUser.default_profile)!)
                        })
                    .catch((error) => console.error(error))
                }
            })
        }
    }, [authenticated, user, setUser, handleProfileChange, locale])

    // Отслеживание изменений локали для перезагрузки профилей
    useEffect(() => {
        if (authenticated && user && lastLocale !== locale) {
            console.debug("Locale changed from", lastLocale, "to", locale, "- reloading profiles")
            setLastLocale(locale)
            
            fetchWithToken(getConfigProfile())
                .then((response) => {
                    const profiles = (response.data as UserProfileTypeEntity[]).filter((profile) => user.user.profiles.includes(profile.id))
                    const userData = {
                        user: user.user,
                        profiles: profiles
                    } as UserProps
                    setUser(userData)
                })
                .catch((error) => console.error(error))
        }
    }, [authenticated, user, locale, lastLocale, setUser])

    return (
        <Layout appBarAlwaysOn menu={(props) => <MainMenu {...props} onProfileChange={handleProfileChange} />}>
            <MainContainer>{children}</MainContainer>
            <Notification />
        </Layout>
    )
}

export const TabWithIcon = styled(Tab)<TabProps>(({ theme }) => ({
    flexDirection: 'row',
    minHeight: 'auto',
    top: theme.spacing(0.5),
    '& .MuiTab-iconWrapper': {
        marginBottom: 0,
        marginRight: theme.spacing(1),
    },
}))

export function RecordActionsTabs({ subTabs = [], resourceParent = undefined, resourceParentId = undefined, onCreateClick }: RecordActionsSubTabsProps): JSX.Element {
    const resource = useResourceContext()
    const resPath = resourceParent ? resourceParent : resource
    const recordId = useGetRecordId()
    const navigate = useNavigate()
    const location = useLocation()
    const translate = useTranslate()

    const [anchor, setAnchor] = useState<null | HTMLElement>(null)
    const showPath = `/${resPath}/${resourceParentId ? resourceParentId : recordId}/show`
    const auditPath = `/${resPath}/${resourceParentId ? resourceParentId : recordId}/audit`
    const editPath = `/${resPath}/${resourceParentId ? resourceParentId : recordId}`

    const getCurrentTab = () => {
        const start = 3
        const indexTab = subTabs?.findIndex(tab => location.pathname.includes(`/${tab.resource}/${tab.id ? tab.id : recordId}/${tab.name}`))
        return indexTab !== -1 ? (indexTab + start) : 2
    }
    const currentTab = getCurrentTab()

    const handleListClick = () => {
        navigate(`/${resPath}`)
    }

    const handleCreateClick = () => {
        if (onCreateClick) {
            onCreateClick()
        } else {
            navigate(`/${resPath}/create`)
        }
    }

    const handleCombinedTabClick = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
        if (!recordId) return
        setAnchor(event.currentTarget)
    }

    const handleMenuClose = () => {
        setAnchor(null)
    }

    const handleShow = () => {
        navigate(showPath)
        handleMenuClose()
    }

    const handleAudit = () => {
        navigate(auditPath)
        handleMenuClose()
    }

    const handleEdit = () => {
        navigate(editPath)
        handleMenuClose()
    }

    const multiTabs = () => {
        //if (subTabs?.length > 0){
            return <>
                <span>{translate("internal.settings")}</span>
                {Boolean(anchor) ? <ExpandLess/>  : <ExpandMore/>}
            </>
        //}
        //return <>{translate("internal.settings")}</>
    }

    return (
        <>
            <AppBar position="static" color="default">
                <Tabs
                    value={currentTab}
                    indicatorColor="primary"
                    textColor="primary"
                >
                    <TabWithIcon label={translate("internal.list")} onClick={handleListClick} icon={<List />}/>
                    <TabWithIcon label={translate("internal.create")} onClick={handleCreateClick} icon={<Create/>}/>
                    <TabWithIcon label={multiTabs()} onClick={handleCombinedTabClick} icon={<Settings/>}/>
                    {
                        subTabs?.map((tab) => {
                            return <TabWithIcon label={tab.label} onClick={() => navigate(`/${tab?.resource}/${tab.id ? tab.id : recordId}/${tab?.name}`)} icon={tab?.icon}/>
                        })
                    }
                </Tabs>
            </AppBar>
            <Menu
                anchorEl={anchor}
                open={Boolean(anchor)}
                onClose={handleMenuClose}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'center',
                }}
                transformOrigin={{
                    vertical: 'top',
                    horizontal: 'center',
                }}
            >
                <MenuItem 
                    selected={location.pathname === showPath} 
                    onClick={handleShow}
                >
                    {translate("internal.show")}
                </MenuItem>
                <MenuItem 
                    selected={location.pathname === editPath} 
                    onClick={handleEdit}
                >
                    {translate("internal.edit")}
                </MenuItem>
                {AUDIT_ENABLED_RESOURCES.includes(resPath!) && (
                    <MenuItem 
                        selected={location.pathname === auditPath} 
                        onClick={handleAudit}
                    >
                        {translate("internal.audit")}
                    </MenuItem>
                )}
            </Menu>
        </>
    )
}

export function NewActionsTabs (): JSX.Element {
    const resource = useResourceContext()
    const navigate = useNavigate()
    const translate = useTranslate()

    return (
        <AppBar position="static" color="default">
            <Tabs value={1} indicatorColor="primary" textColor="primary">
                <TabWithIcon label={translate("internal.list")} onClick={() => navigate(`/${resource}`)} icon={<List/>}/>
                <TabWithIcon label={translate("internal.create")} onClick={() => navigate(`/${resource}/create`)} icon={<Create/>}/>
            </Tabs>
        </AppBar>
    )
}


function ReferenceShowLink (props: { reference: string }): JSX.Element { 
    const record = useRecordContext()
    const {reference} = props
    if (!record) return <></>

    return (
        <Chip
            component={Link}
            to={`/${reference}/${record.id}/show`}
            label={record.name}
            clickable
            color="default"
        />

    )
}

export function ReferenceArrayFieldShowLink(props: {source: string, reference: string}): JSX.Element { 
    const {source, reference} = props
    return (
    <ReferenceArrayField source={source} reference={reference}>
        <SingleFieldList>
            <ReferenceShowLink reference={reference} />
        </SingleFieldList>
    </ReferenceArrayField> )
}
